import React from 'react';
import { AlertTriangle, X, XCircle } from 'lucide-react';

interface ModalAnularFacturaProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  numeroFactura: string;
  totalItems: number;
  isLoading?: boolean;
}

const ModalAnularFactura: React.FC<ModalAnularFacturaProps> = ({
  isOpen,
  onClose,
  onConfirm,
  numeroFactura,
  totalItems,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Anular Factura
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            ¿Está seguro que desea anular la factura{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {numeroFactura}
            </span>
            ?
          </p>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                  Esta acción realizará lo siguiente:
                </p>
                <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 list-disc list-inside">
                  <li>La factura será marcada como anulada</li>
                  <li>
                    Se revertirá el stock de {totalItems}{' '}
                    {totalItems === 1 ? 'ingrediente' : 'ingredientes'}
                  </li>
                  <li>Se registrarán los movimientos de stock negativos</li>
                  <li>Esta acción no se puede deshacer</li>
                </ul>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400">
            El stock de los ingredientes será descontado automáticamente. Por
            favor, confirme que desea continuar con esta operación.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                     bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                     rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 
                     disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 
                     rounded-lg hover:bg-red-700 disabled:opacity-50 
                     disabled:cursor-not-allowed transition-colors
                     flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Anulando...
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4" />
                Anular Factura
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalAnularFactura;
